import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Box,
  Typography,
  Grid,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  CircularProgress,
  Pagination,
  Card,
  Button,
  keyframes,
  Drawer,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Star,
  LocationOn,
  Verified,
  WorkOutline,
  AttachMoney,
  FilterList,
  Close,
  Favorite,
  FavoriteBorder,
} from '@mui/icons-material';
import clientService from '../../services/clientService';
import BookingModal from '../../components/BookingModal';
import { toast } from 'react-toastify';

import { axelionColors } from '../../theme/axelionTheme';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

/**
 * MaslaXat Premium Lawyers Page
 * Elegant, minimalist design with gold accents
 */
const LawyersPageGlass = () => {
  const navigate = useNavigate();
  const { specializations } = useSelector((state) => state.specializations);
  const isMobile = useMediaQuery('(max-width:767px)');

  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [favoriteLawyers, setFavoriteLawyers] = useState(new Set());

  const [filters, setFilters] = useState({
    specialization: '',
    minRating: 0,
    priceRange: [0, 500000],
    experience: '',
    sortBy: 'rating',
  });

  useEffect(() => {
    fetchLawyers();
    loadFavorites();
  }, [filters, currentPage, searchQuery]);

  const loadFavorites = async () => {
    try {
      const favorites = await clientService.favorites.getFavorites();
      const favoriteIds = new Set(favorites.map((f) => f.id));
      setFavoriteLawyers(favoriteIds);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const fetchLawyers = async () => {
    try {
      setLoading(true);
      const response = await clientService.lawyers.searchLawyers({
        ...filters,
        search: searchQuery,
        page: currentPage,
        limit: 9,
      });

      setLawyers(response.lawyers || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Error fetching lawyers:', error);
      setLawyers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      specialization: '',
      minRating: 0,
      priceRange: [0, 500000],
      experience: '',
      sortBy: 'rating',
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleBookConsultation = (lawyer) => {
    setSelectedLawyer(lawyer);
    setBookingModalOpen(true);
  };

  const handleCloseBookingModal = () => {
    setBookingModalOpen(false);
    setSelectedLawyer(null);
  };

  const handleViewProfile = (lawyerId) => {
    navigate(`/lawyers/${lawyerId}`);
  };

  const handleToggleFavorite = async (e, lawyerId) => {
    e.stopPropagation();
    try {
      if (favoriteLawyers.has(lawyerId)) {
        await clientService.favorites.removeFavorite(lawyerId);
        setFavoriteLawyers((prev) => {
          const next = new Set(prev);
          next.delete(lawyerId);
          return next;
        });
        toast.success('Удалено из избранного');
      } else {
        await clientService.favorites.addFavorite(lawyerId);
        setFavoriteLawyers((prev) => new Set(prev).add(lawyerId));
        toast.success('Добавлено в избранное');
      }
    } catch (error) {
      toast.error('Ошибка при изменении избранного');
    }
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: axelionColors.bgLight,
      borderRadius: '8px',
      '& fieldset': {
        borderColor: axelionColors.borderLight,
      },
      '&:hover fieldset': {
        borderColor: axelionColors.gold,
      },
      '&.Mui-focused fieldset': {
        borderColor: axelionColors.gold,
        borderWidth: 1,
      },
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: axelionColors.gold,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: axelionColors.bgCream, pb: 6 }}>
      {/* Mobile Filter Drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: '16px 16px 0 0',
              maxHeight: '80vh',
              bgcolor: axelionColors.bgLight,
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: axelionColors.textDark,
                }}
              >
                Фильтры
              </Typography>
              <IconButton size="small" onClick={() => setFilterDrawerOpen(false)}>
                <Close fontSize="small" sx={{ color: axelionColors.textDark }} />
              </IconButton>
            </Box>

            {/* Specialization Filter */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ '&.Mui-focused': { color: axelionColors.gold } }}>Специализация</InputLabel>
              <Select value={filters.specialization} label="Специализация" onChange={(e) => handleFilterChange('specialization', e.target.value)} sx={{ bgcolor: axelionColors.bgLight, borderRadius: '8px', '& .MuiOutlinedInput-notchedOutline': { borderColor: axelionColors.borderLight }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: axelionColors.gold }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: axelionColors.gold, borderWidth: 1 } }}>
                <MenuItem value="">Все</MenuItem>
                {specializations.filter((s) => s.active).map((spec) => (<MenuItem key={spec.id} value={spec.id}>{spec.name}</MenuItem>))}
              </Select>
            </FormControl>

            {/* Rating Filter */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontWeight: 500, fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: axelionColors.textMuted, mb: 2 }}>Минимальный рейтинг</Typography>
              <Rating value={filters.minRating} onChange={(e, value) => handleFilterChange('minRating', value || 0)} precision={0.5} sx={{ '& .MuiRating-iconFilled': { color: axelionColors.gold }, '& .MuiRating-iconEmpty': { color: axelionColors.borderLight } }} />
            </Box>

            {/* Sort */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ '&.Mui-focused': { color: axelionColors.gold } }}>Сортировка</InputLabel>
              <Select value={filters.sortBy} label="Сортировка" onChange={(e) => handleFilterChange('sortBy', e.target.value)} sx={{ bgcolor: axelionColors.bgLight, borderRadius: '8px', '& .MuiOutlinedInput-notchedOutline': { borderColor: axelionColors.borderLight }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: axelionColors.gold, borderWidth: 1 } }}>
                <MenuItem value="rating">По рейтингу</MenuItem>
                <MenuItem value="price-asc">По цене (возрастание)</MenuItem>
                <MenuItem value="price-desc">По цене (убывание)</MenuItem>
                <MenuItem value="experience">По опыту</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button fullWidth onClick={() => { handleClearFilters(); setFilterDrawerOpen(false); }} sx={{ border: `1px solid ${axelionColors.borderLight}`, color: axelionColors.textSecondary, borderRadius: '8px', py: 1.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.75rem', '&:hover': { borderColor: axelionColors.gold, bgcolor: axelionColors.bgWarm } }}>
                Сбросить
              </Button>
              <Button fullWidth onClick={() => setFilterDrawerOpen(false)} sx={{ bgcolor: axelionColors.gold, color: '#FFFFFF', borderRadius: '8px', py: 1.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.75rem', boxShadow: 'none', '&:hover': { bgcolor: axelionColors.goldDark, boxShadow: 'none' } }}>
                Применить
              </Button>
            </Box>
          </Box>
        </Drawer>
      )}

      {/* Header */}
      <Box
        sx={{
          bgcolor: axelionColors.bgLight,
          borderBottom: `1px solid ${axelionColors.borderLight}`,
          py: { xs: 2, md: 4 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 }, mb: { xs: 2, md: 3 } }}>
            <IconButton
              onClick={() => navigate('/dashboard')}
              sx={{
                border: `1px solid ${axelionColors.borderLight}`,
                bgcolor: axelionColors.bgLight,
                borderRadius: '8px',
                '&:hover': {
                  bgcolor: axelionColors.bgWarm,
                  borderColor: axelionColors.gold,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <ArrowBack sx={{ color: axelionColors.textDark }} />
            </IconButton>
            <Box>
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: { xs: '1.25rem', md: '1.75rem' },
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: axelionColors.textDark,
                  mb: 0.5,
                }}
              >
                Find a Lawyer
              </Typography>
              {!isMobile && (
                <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.9rem' }}>
                  Лучшие юристы для вашего дела
                </Typography>
              )}
            </Box>
          </Box>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Поиск по имени, специализации..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: axelionColors.textMuted }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <Close sx={{ color: axelionColors.textMuted }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputStyles}
          />
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {/* Results Summary */}
        <Card
          sx={{
            mb: 3,
            bgcolor: axelionColors.bgLight,
            border: `1px solid ${axelionColors.borderLight}`,
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
            animation: `${fadeInUp} 0.5s ease-out`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5 }}>
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '0.8rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: axelionColors.textDark,
              }}
            >
              Found: {lawyers.length} lawyers
            </Typography>
            <Button
              startIcon={<FilterList />}
              onClick={() => isMobile ? setFilterDrawerOpen(true) : setShowFilters(!showFilters)}
              sx={{
                border: `1px solid ${axelionColors.borderLight}`,
                color: axelionColors.textDark,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontSize: '0.75rem',
                borderRadius: '8px',
                px: { xs: 2, md: 3 },
                '&:hover': {
                  borderColor: axelionColors.gold,
                  bgcolor: axelionColors.bgWarm,
                },
              }}
            >
              {isMobile ? 'Фильтры' : showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Box>
        </Card>

        <Grid container spacing={3}>
          {/* Filters Sidebar - Desktop/Tablet only */}
          {showFilters && !isMobile && (
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                animation: `${fadeInUp} 0.5s ease-out`,
                animationDelay: '0.1s',
                animationFillMode: 'both',
              }}
            >
              <Card
                sx={{
                  bgcolor: axelionColors.bgLight,
                  border: `1px solid ${axelionColors.borderLight}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
                }}
              >
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        color: axelionColors.textDark,
                      }}
                    >
                      Filters
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleClearFilters}
                      sx={{
                        color: axelionColors.textMuted,
                        '&:hover': {
                          bgcolor: axelionColors.bgWarm,
                          color: axelionColors.gold,
                        },
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Specialization Filter */}
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel sx={{ '&.Mui-focused': { color: axelionColors.gold } }}>
                      Специализация
                    </InputLabel>
                    <Select
                      value={filters.specialization}
                      label="Специализация"
                      onChange={(e) => handleFilterChange('specialization', e.target.value)}
                      sx={{
                        bgcolor: axelionColors.bgLight,
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: axelionColors.borderLight,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: axelionColors.gold,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: axelionColors.gold,
                          borderWidth: 1,
                        },
                      }}
                    >
                      <MenuItem value="">Все</MenuItem>
                      {specializations
                        .filter((s) => s.active)
                        .map((spec) => (
                          <MenuItem key={spec.id} value={spec.id}>
                            {spec.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  {/* Rating Filter */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: axelionColors.textMuted,
                        mb: 2,
                      }}
                    >
                      Минимальный рейтинг
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Rating
                        value={filters.minRating}
                        onChange={(e, value) => handleFilterChange('minRating', value || 0)}
                        precision={0.5}
                        sx={{
                          '& .MuiRating-iconFilled': {
                            color: axelionColors.gold,
                          },
                          '& .MuiRating-iconEmpty': {
                            color: axelionColors.borderLight,
                          },
                        }}
                      />
                      <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.85rem' }}>
                        {filters.minRating || 'Любой'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Price Range Filter */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: axelionColors.textMuted,
                        mb: 2,
                      }}
                    >
                      Диапазон цен
                    </Typography>
                    <Slider
                      value={filters.priceRange}
                      onChange={(e, value) => handleFilterChange('priceRange', value)}
                      valueLabelDisplay="auto"
                      min={0}
                      max={500000}
                      step={10000}
                      sx={{
                        color: axelionColors.gold,
                        '& .MuiSlider-thumb': {
                          bgcolor: axelionColors.gold,
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: `0 0 0 8px ${axelionColors.accentLight}`,
                          },
                        },
                        '& .MuiSlider-track': {
                          bgcolor: axelionColors.gold,
                        },
                        '& .MuiSlider-rail': {
                          bgcolor: axelionColors.borderLight,
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.75rem' }}>
                        {filters.priceRange[0].toLocaleString()} сум
                      </Typography>
                      <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.75rem' }}>
                        {filters.priceRange[1].toLocaleString()} сум
                      </Typography>
                    </Box>
                  </Box>

                  {/* Experience Filter */}
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel sx={{ '&.Mui-focused': { color: axelionColors.gold } }}>
                      Опыт работы
                    </InputLabel>
                    <Select
                      value={filters.experience}
                      label="Опыт работы"
                      onChange={(e) => handleFilterChange('experience', e.target.value)}
                      sx={{
                        bgcolor: axelionColors.bgLight,
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: axelionColors.borderLight,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: axelionColors.gold,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: axelionColors.gold,
                          borderWidth: 1,
                        },
                      }}
                    >
                      <MenuItem value="">Любой</MenuItem>
                      <MenuItem value="0-5">0-5 лет</MenuItem>
                      <MenuItem value="5-10">5-10 лет</MenuItem>
                      <MenuItem value="10-15">10-15 лет</MenuItem>
                      <MenuItem value="15+">15+ лет</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Sort By Filter */}
                  <FormControl fullWidth>
                    <InputLabel sx={{ '&.Mui-focused': { color: axelionColors.gold } }}>
                      Сортировка
                    </InputLabel>
                    <Select
                      value={filters.sortBy}
                      label="Сортировка"
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                      sx={{
                        bgcolor: axelionColors.bgLight,
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: axelionColors.borderLight,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: axelionColors.gold,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: axelionColors.gold,
                          borderWidth: 1,
                        },
                      }}
                    >
                      <MenuItem value="rating">По рейтингу</MenuItem>
                      <MenuItem value="price-asc">По цене (возрастание)</MenuItem>
                      <MenuItem value="price-desc">По цене (убывание)</MenuItem>
                      <MenuItem value="experience">По опыту</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    fullWidth
                    onClick={handleClearFilters}
                    sx={{
                      mt: 3,
                      border: `1px solid ${axelionColors.borderLight}`,
                      color: axelionColors.textSecondary,
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontSize: '0.75rem',
                      borderRadius: '8px',
                      py: 1.25,
                      '&:hover': {
                        borderColor: axelionColors.gold,
                        bgcolor: axelionColors.bgWarm,
                        color: axelionColors.gold,
                      },
                    }}
                  >
                    Reset Filters
                  </Button>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Lawyers Grid */}
          <Grid item xs={12} md={(showFilters && !isMobile) ? 9 : 12}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    border: `2px solid ${axelionColors.borderLight}`,
                    borderTopColor: axelionColors.gold,
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </Box>
            ) : lawyers.length > 0 ? (
              <>
                <Grid container spacing={3}>
                  {lawyers.map((lawyer, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      lg={4}
                      key={lawyer.id}
                      sx={{
                        animation: `${scaleIn} 0.5s ease-out`,
                        animationDelay: `${index * 0.08}s`,
                        animationFillMode: 'both',
                      }}
                    >
                      <Card
                        onClick={() => handleViewProfile(lawyer.id)}
                        sx={{
                          bgcolor: axelionColors.bgLight,
                          border: `1px solid ${axelionColors.borderLight}`,
                          borderRadius: '8px',
                          boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          '&:hover': {
                            boxShadow: '0 8px 24px rgba(26, 26, 26, 0.08)',
                            borderColor: axelionColors.gold,
                            transform: 'translateY(-4px)',
                          },
                        }}
                      >
                        {/* Favorite Icon */}
                        <IconButton
                          onClick={(e) => handleToggleFavorite(e, lawyer.id)}
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: axelionColors.bgLight,
                            border: `1px solid ${axelionColors.borderLight}`,
                            zIndex: 2,
                            '&:hover': {
                              bgcolor: axelionColors.bgWarm,
                              borderColor: axelionColors.gold,
                            },
                          }}
                        >
                          {favoriteLawyers.has(lawyer.id) ? (
                            <Favorite sx={{ color: axelionColors.gold, fontSize: 20 }} />
                          ) : (
                            <FavoriteBorder sx={{ color: axelionColors.textMuted, fontSize: 20 }} />
                          )}
                        </IconButton>

                        <Box sx={{ p: 3, pt: 4 }}>
                          {/* Lawyer Avatar */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <Avatar
                              src={lawyer.avatar}
                              sx={{
                                width: 100,
                                height: 100,
                                border: `3px solid ${axelionColors.gold}`,
                                bgcolor: axelionColors.bgBeige,
                                fontSize: '2rem',
                                fontWeight: 300,
                                color: axelionColors.gold,
                              }}
                            >
                              {lawyer.name?.charAt(0)}
                            </Avatar>
                          </Box>

                          {/* Lawyer Name */}
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: '1.1rem',
                              textAlign: 'center',
                              color: axelionColors.textDark,
                              mb: 1,
                            }}
                          >
                            {lawyer.name}
                          </Typography>

                          {/* Rating */}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Star sx={{ color: axelionColors.gold, fontSize: 20 }} />
                            <Typography sx={{ fontWeight: 500, color: axelionColors.textDark }}>
                              {lawyer.rating || 0}
                            </Typography>
                            <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.85rem' }}>
                              ({lawyer.completedConsultations || 0} отзывов)
                            </Typography>
                          </Box>

                          {/* Specializations */}
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                              mb: 2,
                              justifyContent: 'center',
                            }}
                          >
                            {lawyer.specializations?.slice(0, 2).map((spec, idx) => (
                              <Chip
                                key={idx}
                                label={spec}
                                size="small"
                                sx={{
                                  bgcolor: axelionColors.accentLight,
                                  color: axelionColors.gold,
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  letterSpacing: '0.05em',
                                  borderRadius: '8px',
                                }}
                              />
                            ))}
                            {lawyer.specializations?.length > 2 && (
                              <Chip
                                label={`+${lawyer.specializations.length - 2}`}
                                size="small"
                                sx={{
                                  bgcolor: axelionColors.bgWarm,
                                  color: axelionColors.textSecondary,
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  borderRadius: '8px',
                                }}
                              />
                            )}
                          </Box>

                          {/* Info */}
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <WorkOutline sx={{ fontSize: 18, color: axelionColors.gold }} />
                              <Typography sx={{ color: axelionColors.textSecondary, fontSize: '0.85rem' }}>
                                {lawyer.experience || 0} лет опыта
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <AttachMoney sx={{ fontSize: 18, color: axelionColors.gold }} />
                              <Typography sx={{ color: axelionColors.textSecondary, fontSize: '0.85rem' }}>
                                от {lawyer.priceFrom?.toLocaleString() || 0} сум
                              </Typography>
                            </Box>
                            {lawyer.region && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationOn sx={{ fontSize: 18, color: axelionColors.gold }} />
                                <Typography sx={{ color: axelionColors.textSecondary, fontSize: '0.85rem' }}>
                                  {lawyer.region}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Success Rate */}
                          {lawyer.successRate && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 2,
                                p: 1.5,
                                borderRadius: '8px',
                                bgcolor: axelionColors.successLight,
                                border: `1px solid ${axelionColors.success}30`,
                              }}
                            >
                              <Verified sx={{ color: axelionColors.success, fontSize: 20 }} />
                              <Typography sx={{ fontWeight: 500, color: axelionColors.success, fontSize: '0.85rem' }}>
                                {lawyer.successRate}% успешных дел
                              </Typography>
                            </Box>
                          )}

                          {/* Book Button */}
                          <Button
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBookConsultation(lawyer);
                            }}
                            sx={{
                              bgcolor: axelionColors.gold,
                              color: '#FFFFFF',
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              letterSpacing: '0.1em',
                              fontSize: '0.75rem',
                              borderRadius: '8px',
                              py: 1.5,
                              boxShadow: 'none',
                              '&:hover': {
                                bgcolor: axelionColors.goldDark,
                                boxShadow: 'none',
                              },
                            }}
                          >
                            Book Consultation
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Card
                      sx={{
                        bgcolor: axelionColors.bgLight,
                        border: `1px solid ${axelionColors.borderLight}`,
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={(e, page) => setCurrentPage(page)}
                          sx={{
                            '& .MuiPaginationItem-root': {
                              color: axelionColors.textDark,
                              fontWeight: 500,
                              border: `1px solid ${axelionColors.borderLight}`,
                              borderRadius: '8px',
                              '&:hover': {
                                bgcolor: axelionColors.bgWarm,
                                borderColor: axelionColors.gold,
                              },
                            },
                            '& .Mui-selected': {
                              bgcolor: `${axelionColors.gold} !important`,
                              color: '#FFFFFF',
                              borderColor: axelionColors.gold,
                              '&:hover': {
                                bgcolor: axelionColors.goldDark,
                              },
                            },
                          }}
                        />
                      </Box>
                    </Card>
                  </Box>
                )}
              </>
            ) : (
              <Card
                sx={{
                  bgcolor: axelionColors.bgLight,
                  border: `1px solid ${axelionColors.borderLight}`,
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
                }}
              >
                <Box sx={{ p: 8, textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontWeight: 300,
                      fontSize: '1.25rem',
                      letterSpacing: '0.1em',
                      color: axelionColors.textDark,
                      mb: 2,
                    }}
                  >
                    No Lawyers Found
                  </Typography>
                  <Typography sx={{ color: axelionColors.textMuted, mb: 3 }}>
                    Попробуйте изменить параметры фильтров
                  </Typography>
                  <Button
                    onClick={handleClearFilters}
                    sx={{
                      bgcolor: axelionColors.gold,
                      color: '#FFFFFF',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      fontSize: '0.75rem',
                      borderRadius: '8px',
                      px: 4,
                      py: 1.5,
                      boxShadow: 'none',
                      '&:hover': {
                        bgcolor: axelionColors.goldDark,
                        boxShadow: 'none',
                      },
                    }}
                  >
                    Reset Filters
                  </Button>
                </Box>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Booking Modal */}
      <BookingModal
        open={bookingModalOpen}
        onClose={handleCloseBookingModal}
        lawyer={selectedLawyer}
      />

    </Box>
  );
};

export default LawyersPageGlass;
